import React, { useState, useEffect } from "react";
import axios from "axios";
import { URL } from "../AxiosUrlGlobal";
import "./CrashLogs.css";

function CrashLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalLogs: 0,
    limit: 50,
  });
  const [stats, setStats] = useState({
    total: 0,
    resolved: 0,
    unresolved: 0,
    fatal: 0,
    android: 0,
    ios: 0,
    web: 0,
  });
  const [crashTypesBreakdown, setCrashTypesBreakdown] = useState([]);
  const [recentErrors, setRecentErrors] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    platform: "all",
    crashType: "all",
    resolved: "all",
    isFatal: "all",
    search: "",
    dateFrom: "",
    dateTo: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [resolveNotes, setResolveNotes] = useState("");
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, [
    filters.page,
    filters.limit,
    filters.platform,
    filters.crashType,
    filters.resolved,
    filters.isFatal,
    filters.sortOrder,
  ]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const uid = localStorage.getItem("id");

      const params = { ...filters };
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === "all") {
          delete params[key];
        }
      });

      const response = await axios.post(
        `${URL}/crash-logs`,
        params,
        {
          headers: { token, uid },
        }
      );

      if (response.data.success && response.data.data) {
        setLogs(response.data.data.logs);
        setPagination(response.data.data.pagination);
        setStats(response.data.data.stats);
        setCrashTypesBreakdown(response.data.data.crashTypesBreakdown || []);
        setRecentErrors(response.data.data.recentErrors || []);
      }
    } catch (error) {
      console.error("Error fetching crash logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== "page" ? 1 : value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    fetchLogs();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      handleFilterChange("page", newPage);
    }
  };

  const openDetailModal = (log) => {
    setSelectedLog(log);
    setResolveNotes("");
    setShowDetailModal(true);
  };

  const closeDetailModal = () => {
    setShowDetailModal(false);
    setSelectedLog(null);
    setResolveNotes("");
  };

  const handleResolve = async () => {
    if (!selectedLog) return;

    try {
      setResolving(true);
      const token = localStorage.getItem("token");
      const uid = localStorage.getItem("id");

      const response = await axios.put(
        `${URL}/crash-logs/resolve/${selectedLog._id}`,
        { notes: resolveNotes },
        { headers: { token, uid } }
      );

      if (response.data.success) {
        // Update local state
        setLogs((prev) =>
          prev.map((log) =>
            log._id === selectedLog._id
              ? { ...log, crashData: { ...log.crashData, resolved: true, notes: resolveNotes } }
              : log
          )
        );
        closeDetailModal();
        fetchLogs(); // Refresh stats
      }
    } catch (error) {
      console.error("Error resolving crash:", error);
    } finally {
      setResolving(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Dhaka",
    });
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      android: "android",
      ios: "phone_iphone",
      web: "language",
    };
    return icons[platform] || "devices";
  };

  const getCrashTypeColor = (crashType) => {
    const colors = {
      js_error: "#e74c3c",
      native_crash: "#c0392b",
      unhandled_rejection: "#e67e22",
      component_error: "#9b59b6",
      api_error: "#3498db",
      navigation_error: "#1abc9c",
      render_error: "#e91e63",
    };
    return colors[crashType] || "#95a5a6";
  };

  return (
    <div className="cl-container">
      {/* Header */}
      <div className="cl-header">
        <h1>Crash Logs</h1>
        <p>Monitor and resolve mobile app crashes</p>
      </div>

      {/* Stats */}
      <div className="cl-stats">
        <div className="cl-stat cl-stat-total">
          <span className="material-symbols-outlined">bug_report</span>
          <div>
            <small>Total</small>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="cl-stat cl-stat-unresolved">
          <span className="material-symbols-outlined">error</span>
          <div>
            <small>Unresolved</small>
            <strong>{stats.unresolved}</strong>
          </div>
        </div>
        <div className="cl-stat cl-stat-resolved">
          <span className="material-symbols-outlined">check_circle</span>
          <div>
            <small>Resolved</small>
            <strong>{stats.resolved}</strong>
          </div>
        </div>
        <div className="cl-stat cl-stat-fatal">
          <span className="material-symbols-outlined">dangerous</span>
          <div>
            <small>Fatal</small>
            <strong>{stats.fatal}</strong>
          </div>
        </div>
        <div className="cl-stat cl-stat-android">
          <span className="material-symbols-outlined">android</span>
          <div>
            <small>Android</small>
            <strong>{stats.android}</strong>
          </div>
        </div>
        <div className="cl-stat cl-stat-ios">
          <span className="material-symbols-outlined">phone_iphone</span>
          <div>
            <small>iOS</small>
            <strong>{stats.ios}</strong>
          </div>
        </div>
      </div>

      {/* Recent Errors Summary */}
      {recentErrors.length > 0 && (
        <div className="cl-recent-errors">
          <h3>Top Unresolved Errors</h3>
          <div className="cl-error-list">
            {recentErrors.slice(0, 5).map((error, index) => (
              <div key={index} className="cl-error-item">
                <span className="cl-error-count">{error.count}x</span>
                <span className="cl-error-message">{error._id?.substring(0, 80) || "Unknown error"}...</span>
                <span className="cl-error-screen">{error.screen}</span>
                <span className={`cl-error-platform ${error.platform}`}>
                  <span className="material-symbols-outlined">{getPlatformIcon(error.platform)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="cl-filters">
        <form onSubmit={handleSearch}>
          <div className="cl-filter-row">
            <select
              value={filters.platform}
              onChange={(e) => handleFilterChange("platform", e.target.value)}
            >
              <option value="all">All Platforms</option>
              <option value="android">Android</option>
              <option value="ios">iOS</option>
              <option value="web">Web</option>
            </select>

            <select
              value={filters.crashType}
              onChange={(e) => handleFilterChange("crashType", e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="js_error">JS Error</option>
              <option value="native_crash">Native Crash</option>
              <option value="unhandled_rejection">Unhandled Rejection</option>
              <option value="component_error">Component Error</option>
              <option value="api_error">API Error</option>
              <option value="navigation_error">Navigation Error</option>
              <option value="render_error">Render Error</option>
            </select>

            <select
              value={filters.resolved}
              onChange={(e) => handleFilterChange("resolved", e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="false">Unresolved</option>
              <option value="true">Resolved</option>
            </select>

            <select
              value={filters.isFatal}
              onChange={(e) => handleFilterChange("isFatal", e.target.value)}
            >
              <option value="all">All Severity</option>
              <option value="true">Fatal Only</option>
              <option value="false">Non-Fatal</option>
            </select>

            <select
              value={filters.sortOrder}
              onChange={(e) => handleFilterChange("sortOrder", e.target.value)}
            >
              <option value="desc">Newest</option>
              <option value="asc">Oldest</option>
            </select>

            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
            />

            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
            />

            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Search errors..."
            />

            <button type="submit">
              <span className="material-symbols-outlined">search</span>
            </button>
          </div>
        </form>
      </div>

      {/* Table */}
      <div className="cl-table-container">
        {loading ? (
          <div className="cl-loading">Loading...</div>
        ) : logs.length === 0 ? (
          <div className="cl-empty">
            <span className="material-symbols-outlined">inbox</span>
            <p>No crash logs found</p>
          </div>
        ) : (
          <table className="cl-table">
            <thead>
              <tr>
                <th>Platform</th>
                <th>Type</th>
                <th>Error</th>
                <th>Screen</th>
                <th>Device</th>
                <th>Version</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log._id} className={log.crashData?.resolved ? "resolved" : ""}>
                  <td>
                    <span className={`cl-platform ${log.crashData?.platform}`}>
                      <span className="material-symbols-outlined">
                        {getPlatformIcon(log.crashData?.platform)}
                      </span>
                    </span>
                  </td>
                  <td>
                    <span
                      className="cl-crash-type"
                      style={{ backgroundColor: getCrashTypeColor(log.crashData?.crashType) }}
                    >
                      {log.crashData?.crashType?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="cl-error-cell">
                    <div className="cl-error-text">
                      {log.crashData?.errorMessage?.substring(0, 50) || "-"}
                      {log.crashData?.errorMessage?.length > 50 ? "..." : ""}
                    </div>
                  </td>
                  <td>{log.crashData?.screen || "-"}</td>
                  <td>
                    <div className="cl-device">
                      <span>{log.crashData?.deviceBrand}</span>
                      <small>{log.crashData?.deviceModel}</small>
                    </div>
                  </td>
                  <td>
                    <div className="cl-version">
                      <span>v{log.crashData?.appVersion}</span>
                      <small>({log.crashData?.buildNumber})</small>
                    </div>
                  </td>
                  <td>
                    {log.crashData?.resolved ? (
                      <span className="cl-status resolved">
                        <span className="material-symbols-outlined">check_circle</span>
                      </span>
                    ) : log.crashData?.isFatal ? (
                      <span className="cl-status fatal">
                        <span className="material-symbols-outlined">dangerous</span>
                      </span>
                    ) : (
                      <span className="cl-status pending">
                        <span className="material-symbols-outlined">warning</span>
                      </span>
                    )}
                  </td>
                  <td className="cl-date">{formatDate(log.createdAt)}</td>
                  <td>
                    <button className="cl-view-btn" onClick={() => openDetailModal(log)}>
                      <span className="material-symbols-outlined">visibility</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && logs.length > 0 && (
        <div className="cl-pagination">
          <div className="cl-pagination-info">
            {(pagination.currentPage - 1) * pagination.limit + 1} -{" "}
            {Math.min(pagination.currentPage * pagination.limit, pagination.totalLogs)} of{" "}
            {pagination.totalLogs}
          </div>
          <div className="cl-pagination-controls">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.currentPage === 1}
            >
              <span className="material-symbols-outlined">first_page</span>
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <span className="cl-current-page">
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              <span className="material-symbols-outlined">last_page</span>
            </button>
          </div>
          <select
            value={filters.limit}
            onChange={(e) => handleFilterChange("limit", parseInt(e.target.value))}
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="cl-modal-overlay" onClick={closeDetailModal}>
          <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cl-modal-header">
              <h3>Crash Details</h3>
              <button onClick={closeDetailModal}>
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="cl-modal-body">
              {/* Status Bar */}
              <div className={`cl-detail-status ${selectedLog.crashData?.resolved ? "resolved" : selectedLog.crashData?.isFatal ? "fatal" : "pending"}`}>
                <span className="material-symbols-outlined">
                  {selectedLog.crashData?.resolved ? "check_circle" : selectedLog.crashData?.isFatal ? "dangerous" : "warning"}
                </span>
                <span>
                  {selectedLog.crashData?.resolved
                    ? "Resolved"
                    : selectedLog.crashData?.isFatal
                    ? "Fatal Crash"
                    : "Non-Fatal Error"}
                </span>
              </div>

              {/* Basic Info */}
              <div className="cl-detail-section">
                <h4>Basic Information</h4>
                <div className="cl-detail-grid">
                  <p><strong>Platform:</strong> {selectedLog.crashData?.platform}</p>
                  <p><strong>Type:</strong> {selectedLog.crashData?.crashType?.replace("_", " ")}</p>
                  <p><strong>Screen:</strong> {selectedLog.crashData?.screen || "-"}</p>
                  <p><strong>Action:</strong> {selectedLog.crashData?.action || "-"}</p>
                  <p><strong>User ID:</strong> {selectedLog.userId || "anonymous"}</p>
                  <p><strong>Date:</strong> {formatDate(selectedLog.createdAt)}</p>
                </div>
              </div>

              {/* Device Info */}
              <div className="cl-detail-section">
                <h4>Device Information</h4>
                <div className="cl-detail-grid">
                  <p><strong>Device:</strong> {selectedLog.crashData?.deviceBrand} {selectedLog.crashData?.deviceModel}</p>
                  <p><strong>OS Version:</strong> {selectedLog.crashData?.osVersion}</p>
                  <p><strong>App Version:</strong> v{selectedLog.crashData?.appVersion} ({selectedLog.crashData?.buildNumber})</p>
                  <p><strong>Emulator:</strong> {selectedLog.crashData?.isEmulator ? "Yes" : "No"}</p>
                  <p><strong>Battery:</strong> {selectedLog.crashData?.batteryLevel ? `${selectedLog.crashData.batteryLevel}%` : "-"}</p>
                  <p><strong>Network:</strong> {selectedLog.crashData?.networkState || "-"}</p>
                </div>
              </div>

              {/* Error Message */}
              <div className="cl-detail-section cl-error-section">
                <h4>Error Message</h4>
                <pre className="cl-error-message-full">{selectedLog.crashData?.errorMessage || "No error message"}</pre>
              </div>

              {/* Stack Trace */}
              {selectedLog.crashData?.errorStack && (
                <div className="cl-detail-section">
                  <details open>
                    <summary>Stack Trace</summary>
                    <pre className="cl-stack-trace">{selectedLog.crashData.errorStack}</pre>
                  </details>
                </div>
              )}

              {/* Component Stack */}
              {selectedLog.crashData?.componentStack && (
                <div className="cl-detail-section">
                  <details>
                    <summary>Component Stack</summary>
                    <pre className="cl-stack-trace">{selectedLog.crashData.componentStack}</pre>
                  </details>
                </div>
              )}

              {/* Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="cl-detail-section">
                  <details>
                    <summary>Metadata</summary>
                    <pre>{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
                  </details>
                </div>
              )}

              {/* Resolution Notes */}
              {selectedLog.crashData?.resolved && selectedLog.crashData?.notes && (
                <div className="cl-detail-section cl-resolved-section">
                  <h4>Resolution Notes</h4>
                  <p>{selectedLog.crashData.notes}</p>
                  <small>Resolved at: {formatDate(selectedLog.crashData.resolvedAt)}</small>
                </div>
              )}

              {/* Resolve Form */}
              {!selectedLog.crashData?.resolved && (
                <div className="cl-detail-section cl-resolve-section">
                  <h4>Mark as Resolved</h4>
                  <textarea
                    value={resolveNotes}
                    onChange={(e) => setResolveNotes(e.target.value)}
                    placeholder="Add resolution notes (optional)..."
                    rows={3}
                  />
                  <button
                    className="cl-resolve-btn"
                    onClick={handleResolve}
                    disabled={resolving}
                  >
                    {resolving ? (
                      "Resolving..."
                    ) : (
                      <>
                        <span className="material-symbols-outlined">check_circle</span>
                        Mark as Resolved
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CrashLogs;
